import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ResearchPanel } from "@/components/ResearchPanel";
import { GraduationCap, ShieldAlert, Sparkles, Brain, HeartHandshake, Zap, Target, Ear } from "lucide-react";

const TECHNIQUES = [
  {
    icon: HeartHandshake, tag: "Anxiety", color: "text-secondary", bg: "bg-secondary/15",
    title: "Desensitisation & Counter-Conditioning (DS/CC)",
    desc: "Gradually expose the dog to a trigger at low intensity while pairing it with something great (food/play), changing the emotional response from fear to positive.",
    best: "Fear, noise phobia, stranger/dog anxiety",
  },
  {
    icon: Ear, tag: "Reactivity", color: "text-accent-foreground", bg: "bg-accent/20",
    title: "Look At That (LAT)",
    desc: "Teach the dog to calmly look at a trigger, then back at you for a reward. Builds a positive, controllable pattern around things that used to cause reactions.",
    best: "Leash reactivity, over-arousal",
  },
  {
    icon: Target, tag: "Foundations", color: "text-primary", bg: "bg-primary/15",
    title: "Marker / Clicker Training",
    desc: "A precise marker (click/'yes') tells the dog the exact moment they did the right thing, followed by a reward. Fast, clear, force-free skill building.",
    best: "Any skill, focus, confidence",
  },
  {
    icon: Brain, tag: "Complex", color: "text-secondary", bg: "bg-secondary/15",
    title: "BAT (Behaviour Adjustment Training)",
    desc: "Let the dog observe a trigger from a safe distance and reward calm, self-directed choices to move away — building confidence and decision-making.",
    best: "Fear-based reactivity, frustration",
  },
  {
    icon: Zap, tag: "Anxiety", color: "text-accent-foreground", bg: "bg-accent/20",
    title: "Gradual Separation Protocol",
    desc: "Systematically build tolerance to being alone in tiny increments (seconds → minutes), staying under the anxiety threshold every step.",
    best: "Separation anxiety",
  },
  {
    icon: Sparkles, tag: "Complex", color: "text-primary", bg: "bg-primary/15",
    title: "Management + Enrichment First",
    desc: "Prevent rehearsal of the problem behaviour (baby gates, distance, routine) and meet mental/physical needs with sniffing, foraging and decompression walks.",
    best: "Resource guarding, over-arousal, stress",
  },
];

export default function Training() {
  const [dogs, setDogs] = useState([]);
  useEffect(() => { api.get("/dogs").then((r) => setDogs(r.data)); }, []);

  return (
    <div data-testid="training-page">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-11 w-11 rounded-2xl bg-primary/15 flex items-center justify-center"><GraduationCap className="h-6 w-6 text-primary" /></div>
        <h1 className="font-head text-4xl font-extrabold tracking-tight">Training Techniques</h1>
      </div>
      <p className="text-muted-foreground mb-6 max-w-2xl">Proven, force-free methods — with a focus on dogs who have anxiety and complex behaviour problems. Build a custom AI plan below.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
        {TECHNIQUES.map((t, i) => {
          const Icon = t.icon;
          return (
            <div key={i} data-testid={`technique-${i}`} className="rounded-2xl bg-card border border-border/70 p-5 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all">
              <div className="flex items-center justify-between mb-3">
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${t.bg}`}><Icon className={`h-5 w-5 ${t.color}`} /></div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${t.bg} ${t.color}`}>{t.tag}</span>
              </div>
              <h3 className="font-head font-semibold leading-snug">{t.title}</h3>
              <p className="text-sm text-muted-foreground mt-1.5">{t.desc}</p>
              <div className="text-xs text-muted-foreground mt-3 border-t border-border/60 pt-2"><b className="text-foreground">Best for:</b> {t.best}</div>
            </div>
          );
        })}
      </div>

      <div className="flex items-start gap-2 rounded-xl bg-accent/10 border border-accent/30 px-4 py-3 mb-6 text-sm">
        <ShieldAlert className="h-4 w-4 text-accent-foreground shrink-0 mt-0.5" />
        <p>These techniques are informational. For severe anxiety or aggression, work with a certified force-free trainer or veterinary behaviourist.</p>
      </div>

      <h2 className="font-head text-2xl font-bold tracking-tight mb-4">✨ Build a custom training plan</h2>
      <ResearchPanel
        kind="training"
        dogs={dogs}
        placeholder={{ label: "What do you want to work on?", text: "e.g. My rescue is terrified of the doorbell and barks for 5 minutes. He also can't be left alone without howling..." }}
        extraLabel="What have you already tried? (optional)"
        extraPlaceholder="Previous methods, triggers, environment, other dogs in the home..."
      />
    </div>
  );
}
